/**
 * Quote draft persistence — hydrates from localStorage on mount, writes
 * every change back. Lets a contractor switch tabs or close the browser
 * without losing progress. Storage is best-effort: quota errors silently
 * ignored.
 */
import { useEffect } from 'react';

const DRAFT_KEY = 'qs-quote-draft-v1';

export const readDraft = () => {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

export const draftValue = (key, fallback) => {
  const draft = readDraft();
  return draft && draft[key] !== undefined ? draft[key] : fallback;
};

export const clearDraft = () => {
  try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
};

/**
 * Persists the supplied object to localStorage whenever any of its values
 * change. Callers pass a snapshot of every field they want saved — we
 * stringify and write the lot.
 */
export const useQuoteDraftPersistence = (snapshot) => {
  useEffect(() => {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ ...snapshot, savedAt: Date.now() }));
    } catch { /* ignore quota */ }
    // We intentionally depend on the JSON shape — primitive values inside
    // the snapshot — rather than the object identity. Listing each field
    // would be brittle; consumers always pass the same key order so the
    // serialised string is a stable dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(snapshot)]);
};
